// Sync-status indicator — the header's replacement for the manual Save pill now that saving is
// automatic (calculator/autosave.ts owns the throttle; CalculatorApp owns the state machine;
// this component only renders it).
//
// Lives in the top-right account slot (HeaderRow), the region every Salesforce affordance
// (link / change / save) already occupied. The chip reuses the app's established status-pill
// visual language (mono uppercase, lime/lemon/pom tokens — formerly SaveSummaryDialog's
// CHIP_* constants, moved here when the dialog was deleted).
//
// ONE element, not two: the chip *is* the force-sync affordance whenever an action is available
// (`forceSyncEnabled` / `reconnect`), otherwise it's an inert span. This generalizes what the
// 'reauth' state already did — the earlier separate "Sync now / Synced" pill beside it said the
// same thing twice and ate ~140px of a header row that has no width to spare (see HeaderRow's
// account-slot comment). Every actionable label therefore ends in a verb phrase, which is the
// only signal that the chip is clickable.

import { useEffect, useState } from 'react';
import { sx } from './style-string';
import {
  describeSyncStatus,
  type ChipTone,
  type SyncStatus as SyncStatusValue,
} from './autosave';

const CHIP_BASE =
  'font-family:var(--font-mono);font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;padding:3px 10px;border-radius:999px;white-space:nowrap';

const CHIP_TONES: Record<ChipTone, string> = {
  success: ';background:var(--lime-100);color:var(--lime-400);border:1px solid var(--lime-200)',
  warning: ';background:var(--lemon-100);color:var(--lemon-400);border:1px solid var(--lemon-200)',
  error: ';background:var(--pom-100);color:var(--pom-400);border:1px solid var(--pom-200)',
  muted: ';background:var(--oat-200);color:var(--oat-400);border:1px solid var(--grey-line)',
};

export interface SyncStatusProps {
  status: SyncStatusValue;
  /** Epoch ms of the last successful push; null before the first one. */
  lastSyncedAt: number | null;
  /** Salesforce reconnect URL, present only in the 'reauth' state. */
  oauthUrl: string | null;
  /** Why the last push failed (Salesforce's reasons, one per line): the chip's tooltip in the 'error' state. */
  errorMessage?: string | null;
  onForceSync: () => void;
}

export function SyncStatus({ status, lastSyncedAt, oauthUrl, errorMessage = null, onForceSync }: SyncStatusProps) {
  // A 30s tick keeps "Synced Xm ago" fresh without CalculatorApp re-rendering; only armed
  // while there's a timestamp to age.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== 'synced' || lastSyncedAt == null) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [status, lastSyncedAt]);

  const view = describeSyncStatus(status, lastSyncedAt, now);
  // The chip copy stays short; the reason Salesforce gave is one hover away.
  const title = status === 'error' && errorMessage ? errorMessage : undefined;
  const chipStyle = sx(CHIP_BASE + CHIP_TONES[view.chipTone]);

  // reauth reconnects (opens Salesforce OAuth); every other actionable state force-pushes.
  const reconnect = view.reconnect && oauthUrl != null;
  const action = reconnect
    ? () => window.open(oauthUrl, '_blank', 'noopener')
    : view.forceSyncEnabled
      ? onForceSync
      : null;

  return (
    <div style={sx('display:flex;align-items:center;flex:none')} aria-live="polite">
      {action ? (
        <button
          type="button"
          onClick={action}
          style={sx(CHIP_BASE + CHIP_TONES[view.chipTone] + ';cursor:pointer')}
          aria-label={reconnect ? 'Reconnect Salesforce' : 'Sync to Salesforce now'}
          title={title}
        >
          {view.label}
        </button>
      ) : (
        <span style={chipStyle}>{view.label}</span>
      )}
    </div>
  );
}
